// Utility functions for common operations

import { FILE_UPLOAD, UI_MESSAGES } from '../constants'

/**
 * Format file size from bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Validate uploaded file type and size
 */
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
    const allowedTypes = FILE_UPLOAD.ALLOWED_MIME_TYPES as readonly string[]
    const allowedExtensions = FILE_UPLOAD.ALLOWED_EXTENSIONS

    const isValidType = allowedTypes.includes(file.type) ||
        allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))

    if (!isValidType) {
        return {
            isValid: false,
            error: UI_MESSAGES.UPLOAD.INVALID_FILE_TYPE
        }
    }

    if (FILE_UPLOAD.MAX_FILE_SIZE !== Number.MAX_SAFE_INTEGER && file.size > FILE_UPLOAD.MAX_FILE_SIZE) {
        return { isValid: false, error: UI_MESSAGES.UPLOAD.FILE_TOO_LARGE }
    }

    return { isValid: true }
}

/**
 * Generate mock record count for demo purposes
 */
export const generateMockRecordCount = (): number => {
    return Math.floor(Math.random() * 1000) + 100
}

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString()
}

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
}

/**
 * Check if file is CSV
 */
export const isCsvFile = (file: File): boolean => {
    return file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
}

/**
 * Check if file is Excel
 */
export const isExcelFile = (file: File): boolean => {
    const excelTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]
    const excelExtensions = ['.xlsx', '.xls']

    return excelTypes.includes(file.type) ||
        excelExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
}

/**
 * Get file type display name
 */
export const getFileTypeDisplayName = (file: File): string => {
    if (isCsvFile(file)) return 'CSV'
    if (isExcelFile(file)) return 'Excel'
    return 'Unknown'
}

/**
 * Create a delay for demo purposes
 */
export const delay = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms))
}